/**
 * Categorias da secção "Navegue por categoria" da homepage — fonte única para o site e o painel.
 */

import type { CmsCategory } from './cms-types';

export const HOME_PAGE_CATEGORY_COLOR_CLASSES: Record<
  string,
  {
    bg: string;
    icon: string;
    hover: string;
    badge: string;
    badgeDark: string;
    /** Contorno da badge (cor da categoria). */
    badgeBorder: string;
    hoverBorder: string;
    hoverShadow: string;
  }
> = {
  indigo: {
    bg: 'bg-indigo-50',
    icon: 'text-indigo-400',
    hover: 'group-hover:bg-indigo-100',
    badge: 'bg-indigo-100 text-indigo-700',
    badgeDark: 'bg-indigo-500/20 text-indigo-200',
    badgeBorder: '#818cf8',
    hoverBorder: 'group-hover:border-indigo-400/45 group-hover:ring-indigo-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(129,140,248,0.35)]',
  },
  blue: {
    bg: 'bg-blue-50',
    icon: 'text-blue-400',
    hover: 'group-hover:bg-blue-100',
    badge: 'bg-blue-100 text-blue-700',
    badgeDark: 'bg-blue-500/20 text-blue-200',
    badgeBorder: '#60a5fa',
    hoverBorder: 'group-hover:border-blue-400/45 group-hover:ring-blue-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(96,165,250,0.35)]',
  },
  emerald: {
    bg: 'bg-emerald-50',
    icon: 'text-emerald-400',
    hover: 'group-hover:bg-emerald-100',
    badge: 'bg-emerald-100 text-emerald-700',
    badgeDark: 'bg-emerald-500/20 text-emerald-200',
    badgeBorder: '#34d399',
    hoverBorder: 'group-hover:border-emerald-400/45 group-hover:ring-emerald-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(52,211,153,0.32)]',
  },
  amber: {
    bg: 'bg-amber-50',
    icon: 'text-amber-400',
    hover: 'group-hover:bg-amber-100',
    badge: 'bg-amber-100 text-amber-700',
    badgeDark: 'bg-amber-500/20 text-amber-200',
    badgeBorder: '#fbbf24',
    hoverBorder: 'group-hover:border-amber-400/45 group-hover:ring-amber-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(251,191,36,0.32)]',
  },
  sky: {
    bg: 'bg-sky-50',
    icon: 'text-sky-400',
    hover: 'group-hover:bg-sky-100',
    badge: 'bg-sky-100 text-sky-700',
    badgeDark: 'bg-sky-500/20 text-sky-200',
    badgeBorder: '#38bdf8',
    hoverBorder: 'group-hover:border-sky-400/45 group-hover:ring-sky-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(56,189,248,0.32)]',
  },
  violet: {
    bg: 'bg-violet-50',
    icon: 'text-violet-400',
    hover: 'group-hover:bg-violet-100',
    badge: 'bg-violet-100 text-violet-700',
    badgeDark: 'bg-violet-500/20 text-violet-200',
    badgeBorder: '#a78bfa',
    hoverBorder: 'group-hover:border-violet-400/45 group-hover:ring-violet-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(167,139,250,0.32)]',
  },
  rose: {
    bg: 'bg-rose-50',
    icon: 'text-rose-400',
    hover: 'group-hover:bg-rose-100',
    badge: 'bg-rose-100 text-rose-700',
    badgeDark: 'bg-rose-500/20 text-rose-200',
    badgeBorder: '#fb7185',
    hoverBorder: 'group-hover:border-rose-400/45 group-hover:ring-rose-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(251,113,133,0.32)]',
  },
  cyan: {
    bg: 'bg-cyan-50',
    icon: 'text-cyan-400',
    hover: 'group-hover:bg-cyan-100',
    badge: 'bg-cyan-100 text-cyan-700',
    badgeDark: 'bg-cyan-500/20 text-cyan-200',
    badgeBorder: '#22d3ee',
    hoverBorder: 'group-hover:border-cyan-400/45 group-hover:ring-cyan-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(34,211,238,0.32)]',
  },
  fuchsia: {
    bg: 'bg-fuchsia-50',
    icon: 'text-fuchsia-400',
    hover: 'group-hover:bg-fuchsia-100',
    badge: 'bg-fuchsia-100 text-fuchsia-700',
    badgeDark: 'bg-fuchsia-500/20 text-fuchsia-200',
    badgeBorder: '#e879f9',
    hoverBorder: 'group-hover:border-fuchsia-400/45 group-hover:ring-fuchsia-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(232,121,249,0.32)]',
  },
  teal: {
    bg: 'bg-teal-50',
    icon: 'text-teal-400',
    hover: 'group-hover:bg-teal-100',
    badge: 'bg-teal-100 text-teal-700',
    badgeDark: 'bg-teal-500/20 text-teal-200',
    badgeBorder: '#2dd4bf',
    hoverBorder: 'group-hover:border-teal-400/45 group-hover:ring-teal-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(45,212,191,0.32)]',
  },
  lime: {
    bg: 'bg-lime-50',
    icon: 'text-lime-400',
    hover: 'group-hover:bg-lime-100',
    badge: 'bg-lime-100 text-lime-700',
    badgeDark: 'bg-lime-500/20 text-lime-200',
    badgeBorder: '#a3e635',
    hoverBorder: 'group-hover:border-lime-400/45 group-hover:ring-lime-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(163,230,53,0.32)]',
  },
  green: {
    bg: 'bg-green-50',
    icon: 'text-green-400',
    hover: 'group-hover:bg-green-100',
    badge: 'bg-green-100 text-green-700',
    badgeDark: 'bg-green-500/20 text-green-200',
    badgeBorder: '#4ade80',
    hoverBorder: 'group-hover:border-green-400/45 group-hover:ring-green-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(74,222,128,0.32)]',
  },
  orange: {
    bg: 'bg-orange-50',
    icon: 'text-orange-400',
    hover: 'group-hover:bg-orange-100',
    badge: 'bg-orange-100 text-orange-700',
    badgeDark: 'bg-orange-500/20 text-orange-200',
    badgeBorder: '#fb923c',
    hoverBorder: 'group-hover:border-orange-400/45 group-hover:ring-orange-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(251,146,60,0.32)]',
  },
  yellow: {
    bg: 'bg-yellow-50',
    icon: 'text-yellow-400',
    hover: 'group-hover:bg-yellow-100',
    badge: 'bg-yellow-100 text-yellow-700',
    badgeDark: 'bg-yellow-500/20 text-yellow-200',
    badgeBorder: '#facc15',
    hoverBorder: 'group-hover:border-yellow-400/45 group-hover:ring-yellow-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(250,204,21,0.32)]',
  },
  red: {
    bg: 'bg-red-50',
    icon: 'text-red-400',
    hover: 'group-hover:bg-red-100',
    badge: 'bg-red-100 text-red-700',
    badgeDark: 'bg-red-500/20 text-red-200',
    badgeBorder: '#f87171',
    hoverBorder: 'group-hover:border-red-400/45 group-hover:ring-red-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(248,113,113,0.32)]',
  },
  pink: {
    bg: 'bg-pink-50',
    icon: 'text-pink-400',
    hover: 'group-hover:bg-pink-100',
    badge: 'bg-pink-100 text-pink-700',
    badgeDark: 'bg-pink-500/20 text-pink-200',
    badgeBorder: '#f472b6',
    hoverBorder: 'group-hover:border-pink-400/45 group-hover:ring-pink-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(244,114,182,0.32)]',
  },
  purple: {
    bg: 'bg-purple-50',
    icon: 'text-purple-400',
    hover: 'group-hover:bg-purple-100',
    badge: 'bg-purple-100 text-purple-700',
    badgeDark: 'bg-purple-500/20 text-purple-200',
    badgeBorder: '#c084fc',
    hoverBorder: 'group-hover:border-purple-400/45 group-hover:ring-purple-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(192,132,252,0.32)]',
  },
  slate: {
    bg: 'bg-slate-50',
    icon: 'text-slate-400',
    hover: 'group-hover:bg-slate-100',
    badge: 'bg-slate-100 text-slate-700',
    badgeDark: 'bg-slate-500/20 text-slate-200',
    badgeBorder: '#94a3b8',
    hoverBorder: 'group-hover:border-slate-400/45 group-hover:ring-slate-400/30',
    hoverShadow:
      'group-hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.55),0_0_40px_-8px_rgba(148,163,184,0.32)]',
  },
};

export type HelpCenterHomeCategoryDef = {
  id: string;
  name: string;
  description: string;
  /** Nome do componente Lucide (ver `cms-icons`) */
  icon: string;
  articles: number;
  badge: string | null;
  color: string;
};

/** Ordem e conteúdo iguais aos cartões atuais da homepage. */
export const HELP_CENTER_HOME_CATEGORY_DEFS: HelpCenterHomeCategoryDef[] = [
  {
    id: 'primeiros-passos',
    name: 'Primeiros Passos',
    description:
      'Novo por aqui? Aprenda a usar o chat, enviar mensagens, personalizar seu perfil e muito mais.',
    icon: 'BookOpenCheck',
    articles: 10,
    badge: 'Comece aqui',
    color: 'indigo',
  },
  {
    id: 'account',
    name: 'Gestão de Conta',
    description: 'Configure seu perfil, gerencie credenciais e personalize sua experiência no xat',
    icon: 'UserConfig',
    articles: 12,
    badge: 'Popular',
    color: 'blue',
  },
  {
    id: 'safety',
    name: 'Segurança e Privacidade',
    description:
      'Proteja sua conta, entenda nossas diretrizes e aprenda a denunciar conteúdo impróprio',
    icon: 'Shield',
    articles: 8,
    badge: null,
    color: 'emerald',
  },
  {
    id: 'powers',
    name: 'Powers e Recursos Premium',
    description: 'Desbloqueie funcionalidades exclusivas, personalize seu chat com powers especiais',
    icon: 'PowerSvg',
    articles: 15,
    badge: 'Atualizado',
    color: 'amber',
  },
  {
    id: 'chats',
    name: 'Salas de Chat',
    description: 'Crie, personalize e modere suas próprias salas de chat com ferramentas avançadas',
    icon: 'MessageSquare',
    articles: 10,
    badge: null,
    color: 'sky',
  },
  {
    id: 'store',
    name: 'Loja e Pagamentos',
    description: 'Métodos de pagamento seguros, gerenciamento de compras e políticas de reembolso',
    icon: 'ShoppingBag',
    articles: 7,
    badge: null,
    color: 'violet',
  },
  {
    id: 'troubleshooting',
    name: 'Solução de Problemas',
    description: 'Resolva problemas técnicos, erros comuns e otimize sua experiência no xat',
    icon: 'Wrench',
    articles: 11,
    badge: null,
    color: 'rose',
  },
  {
    id: 'mobile',
    name: 'Aplicativos Mobile',
    description: 'Guias completos para usar xat em dispositivos móveis iOS e Android',
    icon: 'Smartphone',
    articles: 9,
    badge: 'Novo',
    color: 'cyan',
  },
  {
    id: 'xat-live',
    name: 'xat Live',
    description:
      'Chamadas de voz e vídeo, streaming ao vivo e recursos de comunicação em tempo real',
    icon: 'Video',
    articles: 6,
    badge: 'Novo',
    color: 'fuchsia',
  },
];

export const HELP_CENTER_HOME_CATEGORY_IDS = new Set(HELP_CENTER_HOME_CATEGORY_DEFS.map((d) => d.id));

export function homeCategoryDefToCmsSeed(def: HelpCenterHomeCategoryDef): CmsCategory {
  return {
    id: def.id,
    name: def.name,
    description: def.description,
    icon: def.icon,
    badge: def.badge,
    color: def.color,
  };
}

function hasOwn<K extends string>(obj: object, key: K): obj is Record<K, unknown> {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

export function mergeCategoriesWithHomeDefaults(categories: CmsCategory[]): CmsCategory[] {
  const map = new Map(categories.map((c) => [c.id, { ...c }]));
  for (const def of HELP_CENTER_HOME_CATEGORY_DEFS) {
    const existing = map.get(def.id);
    const base = homeCategoryDefToCmsSeed(def);
    if (!existing) {
      map.set(def.id, base);
    } else {
      const badge = hasOwn(existing, 'badge') ? existing.badge ?? null : base.badge ?? null;
      const colorRaw = hasOwn(existing, 'color') ? existing.color : base.color;
      const color =
        colorRaw && String(colorRaw).trim() !== '' ? String(colorRaw) : base.color;
      // Ícones SVG oficiais da homepage (account / powers) — sincroniza mesmo com CMS antigo.
      const forceHomeIcon = def.id === 'account' || def.id === 'powers';
      map.set(def.id, {
        ...existing,
        badge,
        color,
        ...(forceHomeIcon ? { icon: base.icon } : {}),
      });
    }
  }
  const homeOrdered = HELP_CENTER_HOME_CATEGORY_DEFS.map((d) => map.get(d.id)!);
  const extras = categories.filter((c) => !HELP_CENTER_HOME_CATEGORY_IDS.has(c.id));
  return [...homeOrdered, ...extras];
}
